import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TicketFileDto } from '@sib/api';

enum DuplicateDialogType {
  Upload = 'upload',
  Existing = 'existing',
}

type ExistingDuplicate = {
  file: File;
  matchingDocumentIds: string[];
};

export type DuplicateFile = {
  name: string;
  size?: number;
};

type DuplicateDialogState = {
  type: DuplicateDialogType;
  files: DuplicateFile[];
  existingDuplicates?: ExistingDuplicate[];
};

export interface UseTicketDuplicateDialogParams {
  newFiles: File[];
  existingDocuments: TicketFileDto[];
  setDeleteState: (params: {
    fileId: string;
    markedForDelete: boolean;
  }) => void;
  setFilesValue: (files: File[]) => void;
}

export const useTicketDuplicateDialog = ({
  newFiles,
  existingDocuments,
  setDeleteState,
  setFilesValue,
}: UseTicketDuplicateDialogParams) => {
  const [dialogState, setDialogState] = useState<DuplicateDialogState | null>(
    null
  );
  const prevFilesRef = useRef<File[]>([]);
  const onReplaceConfirmRef = useRef<(() => void) | null>(null);

  const normalizeName = useCallback((name?: string | null) => {
    if (!name) return '';

    return name.trim().toLowerCase();
  }, []);

  const byName = useMemo(() => {
    const byNameMap = new Map<string, TicketFileDto[]>();
    existingDocuments.forEach(doc => {
      const key = normalizeName(doc.fileName);
      byNameMap.set(key, [...(byNameMap.get(key) ?? []), doc]);
    });

    return byNameMap;
  }, [existingDocuments, normalizeName]);

  useEffect(() => {
    if (dialogState) return;
    if (newFiles.length === 0 || existingDocuments.length === 0) {
      prevFilesRef.current = newFiles;

      return;
    }

    const prevNames = new Set(
      prevFilesRef.current
        .map(file => normalizeName(file?.name))
        .filter(Boolean)
    );
    const addedFiles = newFiles.filter(file => {
      const normalizedName = normalizeName(file?.name);
      if (!normalizedName) return false;

      return !prevNames.has(normalizedName);
    });

    if (addedFiles.length === 0) {
      prevFilesRef.current = newFiles;

      return;
    }

    const duplicates = addedFiles.flatMap(file => {
      const normalizedName = normalizeName(file?.name);
      if (!normalizedName) return [];

      const docs = byName.get(normalizedName);
      if (!docs?.length) return [];

      return [
        {
          file,
          matchingDocumentIds: docs.map(doc => doc.id),
        },
      ];
    });

    if (duplicates.length > 0) {
      setDialogState({
        type: DuplicateDialogType.Existing,
        files: duplicates.map(dup => ({
          name: dup.file.name,
          size: dup.file.size,
        })),
        existingDuplicates: duplicates,
      });
    }

    prevFilesRef.current = newFiles;
  }, [newFiles, existingDocuments.length, byName, normalizeName, dialogState]);

  const openUploadDialog = useCallback(
    (duplicates: File[], onReplaceConfirm: () => void) => {
      onReplaceConfirmRef.current = onReplaceConfirm;
      setDialogState({
        type: DuplicateDialogType.Upload,
        files: duplicates.map(file => ({
          name: file.name,
          size: file.size,
        })),
      });
    },
    []
  );

  const resetDialogState = useCallback(() => {
    setDialogState(null);
    onReplaceConfirmRef.current = null;
  }, []);

  const handleConfirm = useCallback(() => {
    if (!dialogState) return;

    if (dialogState.type === DuplicateDialogType.Upload) {
      onReplaceConfirmRef.current?.();
      resetDialogState();

      return;
    }

    if (dialogState.type === DuplicateDialogType.Existing) {
      dialogState.existingDuplicates?.forEach(duplicate => {
        duplicate.matchingDocumentIds.forEach(id =>
          setDeleteState({ fileId: id, markedForDelete: true })
        );
      });
      resetDialogState();
    }
  }, [dialogState, setDeleteState, resetDialogState]);

  const handleCancel = useCallback(() => {
    if (!dialogState) return;

    if (dialogState.type === DuplicateDialogType.Existing) {
      const duplicateNames = new Set(
        dialogState.existingDuplicates
          ?.map(dup => normalizeName(dup.file?.name))
          .filter(Boolean)
      );
      const filteredFiles = newFiles.filter(file => {
        const normalizedName = normalizeName(file?.name);
        if (!normalizedName) return true;

        return !duplicateNames.has(normalizedName);
      });
      setFilesValue(filteredFiles);
      prevFilesRef.current = filteredFiles;
    }

    resetDialogState();
  }, [dialogState, newFiles, normalizeName, resetDialogState, setFilesValue]);

  const handleOpenChange = useCallback(
    (open: boolean) => !open && resetDialogState(),
    [resetDialogState]
  );

  return {
    dialogOpen: dialogState !== null,
    dialogFiles: dialogState?.files ?? [],
    openUploadDialog,
    handleConfirm,
    handleCancel,
    handleOpenChange,
  };
};
